import { Box, BoxProps, IconButton, IconButtonProps, Text } from '@chakra-ui/react';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { Brand, Brands, Share } from '@waitroom/models';
import { addQueryParams, keysOf } from '@waitroom/utils';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getIcon, icons } from '../../../../../constants/brand';
import theme from '../../../../theme';
import { Icon } from '../../Icon/Icon';

const defaultButtonProps = {
  isRound: true,
  boxShadow: 'none',
  variant: 'unstyled',
  color: 'white',
};

export interface ShareLinksProps {
  url: string;
  iconButtonProps?: Partial<IconButtonProps>;
  containerProps?: BoxProps;
  showName?: boolean;
  onCopyLink?: () => void;
}

const ShareLinks = ({
  url,
  iconButtonProps,
  containerProps,
  showName,
  onCopyLink,
}: ShareLinksProps): ReactElement | null => {
  const { t } = useTranslation();

  const shareProps = useMemo(() => {
    const shareText = t('global.shareText');
    return {
      [Brand.LINKEDIN]: {
        url: addQueryParams(url, { utm_source: Brand.LINKEDIN }),
      },
      [Brand.TWITTER]: {
        url,
        text: `${shareText}: ${addQueryParams(url, {
          utm_source: Brand.TWITTER,
        })}`,
      },
      [Brand.FACEBOOK]: {
        url: addQueryParams(url, { utm_source: Brand.FACEBOOK }),
      },
      [Brand.WHATSAPP]: {
        text: `${shareText}: ${decodeURIComponent(
          addQueryParams(url, { utm_source: Brand.WHATSAPP }),
        )}`,
      },
    };
  }, [t, url]);

  return (
    <>
      {onCopyLink ? (
        <Box pb={4} {...containerProps}>
          <IconButton
            {...iconButtonProps}
            variant="outline"
            colorScheme="gray.300"
            borderColor="gray.50"
            aria-label={t('global.copyLink')}
            onClick={() => {
              onCopyLink();
            }}
            boxShadow="none"
            isRound
          >
            <Icon icon={faLink} color={theme.colors.gray['900']} style={{ fontSize: '.85em' }} />
          </IconButton>
          {showName ? (
            <Text display={{ base: 'none', sm: 'block' }} fontSize="sm" pt={2} color="gray.700">
              {t('global.copyLink')}
            </Text>
          ) : null}
        </Box>
      ) : null}
      {keysOf(Share).map((key) => (
        <Box key={Brands[key].name} pb={4} {...containerProps}>
          <a
            href={Share[key](shareProps[key])}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="simple"
          >
            <IconButton
              {...defaultButtonProps}
              bg={Brands[key].color}
              aria-label={Brands[key].name}
              size="sm"
              {...iconButtonProps}
            >
              {getIcon(icons[key])}
            </IconButton>
            {showName ? (
              <Text display={{ base: 'none', sm: 'block' }} fontSize="sm" pt={2} color="gray.700">
                {Brands[key].name}
              </Text>
            ) : null}
          </a>
        </Box>
      ))}
    </>
  );
};
export default ShareLinks;
